import { useEmailDomainHealth } from '@/hooks/useEmailDomainHealth';
import type { DomainCheckStatus } from '@/lib/emailDomainHealth';
import { PENDING_CHECKS } from '@/lib/emailDomainHealth';

type CoachEmailDomainHealthCardProps = {
  email: string | null;
  onRequestSkip: () => void;
  skipDisabled: boolean;
  skipPending: boolean;
  alreadySkipped: boolean;
};

function statusIcon(status: DomainCheckStatus): string {
  switch (status) {
    case 'ok':
      return '✓';
    case 'fail':
      return '✕';
    case 'warn':
      return '!';
    default:
      return '…';
  }
}

function statusClass(status: DomainCheckStatus): string {
  switch (status) {
    case 'ok':
      return 'text-emerald-700';
    case 'fail':
      return 'text-red-700';
    case 'warn':
      return 'text-amber-700';
    default:
      return 'text-gray-400';
  }
}

export function CoachEmailDomainHealthCard({
  email,
  onRequestSkip,
  skipDisabled,
  skipPending,
  alreadySkipped,
}: CoachEmailDomainHealthCardProps) {
  const { health, checking, recheck } = useEmailDomainHealth(email);

  const showSkip = health.hasFailure && !alreadySkipped;
  const checks =
    health.checks.length > 0 ? health.checks : PENDING_CHECKS;

  return (
    <div className='rounded-lg border border-gray-200 p-3 space-y-2'>
      <div className='flex items-center justify-between gap-2'>
        <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
          Email domain
        </p>
        <button
          type='button'
          disabled={checking || !email}
          onClick={recheck}
          className='text-xs font-semibold text-primary hover:underline disabled:opacity-50'
        >
          {checking ? 'Checking…' : 'Recheck'}
        </button>
      </div>
      <p className='text-xs text-gray-500'>
        {health.domain ? (
          <>
            <span className='font-mono text-gray-700'>{health.domain}</span>
            {' · '}
          </>
        ) : null}
        DNS and reachability checks run in your browser (not the server).
      </p>
      <ul className='space-y-1'>
        {checks.map((check) => (
          <li
            key={check.id}
            className='flex items-start gap-2 text-xs text-gray-800'
          >
            <span
              className={`shrink-0 font-bold w-4 text-center ${statusClass(
                check.status
              )}`}
              aria-hidden
            >
              {statusIcon(check.status)}
            </span>
            <span className='min-w-0'>
              <span className='font-semibold text-gray-600'>{check.label}</span>
              <span className='text-gray-500'> — {check.detail}</span>
            </span>
          </li>
        ))}
      </ul>
      {showSkip ? (
        <div className='flex flex-wrap items-center gap-2 pt-1'>
          <button
            type='button'
            disabled={skipDisabled || skipPending}
            onClick={onRequestSkip}
            className='px-3 py-1.5 text-xs font-semibold rounded-md border border-amber-300 text-amber-800 hover:bg-amber-50 disabled:opacity-50'
          >
            {skipPending ? 'Skipping…' : 'Skip this touch'}
          </button>
          <p className='text-xs text-gray-500'>
            Domain looks stale; skip so it leaves your review queue.
          </p>
        </div>
      ) : null}
      {alreadySkipped ? (
        <p className='text-xs text-amber-800'>This touch is already skipped.</p>
      ) : null}
    </div>
  );
}
